import DadosSocio from './dados-socio.model';
import Endereco from './endereco.model';
import { utils } from "../../../../util/utils";

/**
 * Modelo de objeto para Dados da empresa do eLegalize;
 * @class DadosEmpresa
 * @namespace DadosEmpresa
 */
export default class DadosEmpresa {
  _capitalSocial = null;
  _faturamentoMensal = null;

  set capitalSocial(valor) {
    this._capitalSocial = utils.valueMaskToNumber(valor);
  }

  get capitalSocial() {
    return this._capitalSocial;
  }

  set faturamentoMensal(valor) {
    this._faturamentoMensal = utils.valueMaskToNumber(valor);
  }

  get faturamentoMensal() {
    return this._faturamentoMensal;
  }

  /**
   * @param {Number} id - Id do formulário
   * @param {String} nomeFantasia - Nome fantasia
   * @param {Array} nomeEmpresarial1 - Sugestão 1 de nome empresarial
   * @param {String} nomeEmpresarial2 - Sugestão 2 de nome empresarial
   * @param {String} nomeEmpresarial3 - Sugestão 3 de nome empresarial
   * @param {String} iptuEmpresa = Numero do IPTU da empresa
   * @param {Endereco} endereco - Endereço
   * @param {Number} quantidadeDeSocios - Quantidade de sócios
   * @param {Number} capitalSocial - Valor capital social da empresa
   * @param {Number} faturamentoMensal - Estimativa de faturamento mensal
   * @param {DadosSocio[]} socios - Array de Dados de Socios
   * @param {Array} anexos Array de anexos
   * @param {Number} empresaId - Id da empresa
   * @param {Number} processoId - Id do processo
   * @constructor
   */
  constructor(
    id = null,
    nomeFantasia = '',
    nomeEmpresarial1 = '',
    nomeEmpresarial2 = '',
    nomeEmpresarial3 = '',
    iptuEmpresa = '',
    endereco = new Endereco(),
    quantidadeDeSocios = null,
    capitalSocial = null,
    faturamentoMensal = null,
    socios = [],
    anexos = [],
    empresaId = null,
    processoId = null
  ) {
    this.id = id;
    this.nomeFantasia = nomeFantasia;
    this.nomeEmpresarial1 = nomeEmpresarial1;
    this.nomeEmpresarial2 = nomeEmpresarial2;
    this.nomeEmpresarial3 = nomeEmpresarial3;
    this.iptuEmpresa = iptuEmpresa;
    this.endereco = endereco;
    this.quantidadeDeSocios = quantidadeDeSocios;
    this.capitalSocial = capitalSocial;
    this.faturamentoMensal = faturamentoMensal;
    this.socios = socios;
    this.anexos = anexos;
    this.empresaId = empresaId;
    this.processoId = processoId;
  }

  /**
   * Atualiza modelo com dados válidos de um objeto com dados de
   * formulário;
   * @param {Object} formData Objeto com dados do formulário
   */
  updateDados(formData) {
    for (let propriedade in formData) {
      if (!Object.keys(formData).includes(propriedade)) {
        console.warn(`WARN: Propriedade "${propriedade}" não é valido para modelo`);
        continue;
      }
      this[propriedade] = formData[propriedade];
    }
  }

  /**
   * Formata e trata, se necessário dados do modelo para ser inserido no body de um request
   * @returns {Object} Objeto formatado para body da requisição;
   * @TODO Quando endpoint for criado verificar e ajustar.
   */
  asRequestBody(cleanBody = true) {
    const body = {
      id: this.id,
      nomeFantasia: this.nomeFantasia,
      sugestoesNomeEmpresarial: [this.nomeEmpresarial1, this.nomeEmpresarial2, this.nomeEmpresarial3],
      quantidadeDeSocios: this.quantidadeDeSocios,
      capitalSocial: this.capitalSocial,
      iptuEmpresa: this.iptuEmpresa,
      faturamentoMensal: this.faturamentoMensal,
      socios: this.sociosAsRequestBody(cleanBody),
      anexos: this.anexos,
      empresa: {
        id: this.empresaId
      },
      processoId: this.processoId,
      ...this.endereco.asRequestBody(cleanBody)
    };

    if(cleanBody) {
      this.cleanBody(body);
    }

    return body;
  }

  /**
   * Limpa do objeto campos vazios antes de enviar o request para o back;
   * @param {Object} body - body do request
   */
  cleanBody(body) {
    for (const prop in body) {
      if (body[prop] === '' || body[prop] === null || (Array.isArray(body[prop]) && body[prop].length == 0)) {
        delete body[prop];
      }
    }
  }

  sociosAsRequestBody(cleanBody = true) {
    const lista = [];
    for (const socio in this.socios) {
      const socioModelo = new DadosSocio();
      socioModelo.updateDados(this.socios[socio]);
      lista.push(socioModelo.asRequestBody(cleanBody));
    }
    return lista;
  }

  /**
   * Formata e trata, se necessário, dados da resposta para o modelo
   */
  asModel(response) {
    const objetoEmpresa = {
      ...response.empresa,
      pais: response.pais,
      cidade: response.cidade
    };

    this.id = response.id;
    this.empresaId = objetoEmpresa.id;
    this.nomeFantasia = objetoEmpresa.nomeFantasia;
    this.iptuEmpresa = response.iptuEmpresa;
    response.sugestoesNomeEmpresarial.forEach((nome, index) => {
      this[`nomeEmpresarial${index + 1}`] = nome;
    });
    this.endereco.asModel(objetoEmpresa);
    this.quantidadeDeSocios = !response.quantidadeDeSocios? null : response.quantidadeDeSocios.toString();
    this.capitalSocial = utils.valueMaskToNumber(objetoEmpresa.capitalSocial);
    this.faturamentoMensal = utils.valueMaskToNumber(response.faturamentoMensal);
    this.socios = this.socioAsModel(response);
    this.anexos = response.anexos ? [...response.anexos] : [];
    this.processoId = response.processoId;
  }

  /**
   * Formata e trata, se necessário, dados da resposta para o modelo de sócio
   * @returns {Array} Lista de sócio formatado como o modelo;
   */
  socioAsModel(response) {
    const lista = [];
    for (const socio in response.socios) {
      const socioModelo = new DadosSocio();
      socioModelo.asModel(response.socios[socio]);
      lista.push(socioModelo);
    }
    return lista;
  }
}
